import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Container,
  Divider,
  Avatar,
  List,
  ListItem,
  ListItemText,
  Chip,
  Button,
  Card,
  CardContent,
  TextField,
  Switch,
  FormControlLabel,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import PrintIcon from '@mui/icons-material/Print';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import GavelIcon from '@mui/icons-material/Gavel';
import ManagerApi from '../../api/ManagerApi';
import CourtApi from '../../api/CourtApi';
import ProsecutorApi from '../../api/ProsecutorApi';
import {
  COURT_FILING_METHOD_LABELS,
  CourtFilingMethod,
  JP_PRECINCT_OPTIONS,
  describeJpPrecinct,
  FullCourtDto,
  PROSECUTOR_CONTACT_LABELS,
  ProsecutorContactPreference,
  ProsecutorDto,
  TraitLists,
} from '../../interfaces/CourtInterfaces';
import './ManagerCourtView.scss';
import CourtClientsPanel from '../../components/CourtClientsPanel/CourtClientsPanel';
import TraitPicker from '../../components/TraitPicker/TraitPicker';
import CourtRatingsCard from '../../components/CourtRatingsCard/CourtRatingsCard';
import CourtCoveringAttorneys from '../../components/CourtRatingsCard/CourtCoveringAttorneys';

/** The prosecutor dialog's fields. Text inputs hold '' for "not recorded"; null goes to the server. */
const EMPTY_PROSECUTOR_FORM = {
  Prosecutor_Name: '',
  Prosecutor_Email: '',
  Prosecutor_Phone: '',
  Prosecutor_Disposition: '',
  Prosecutor_Traits: [] as string[],
  Prosecutor_TraitsOtherNegative: '',
  Prosecutor_TraitsOtherPositive: '',
  Prosecutor_PreferredContact: '' as ProsecutorContactPreference | '',
  Prosecutor_TextNumber: '',
  Prosecutor_Active: true,
};

type ProsecutorForm = typeof EMPTY_PROSECUTOR_FORM;

function prosecutorFormFrom(p: ProsecutorDto): ProsecutorForm {
  return {
    Prosecutor_Name: p.Prosecutor_Name,
    Prosecutor_Email: p.Prosecutor_Email,
    Prosecutor_Phone: p.Prosecutor_Phone || '',
    Prosecutor_Disposition: p.Prosecutor_Disposition || '',
    Prosecutor_Traits: p.Prosecutor_Traits ?? [],
    Prosecutor_TraitsOtherNegative: p.Prosecutor_TraitsOtherNegative || '',
    Prosecutor_TraitsOtherPositive: p.Prosecutor_TraitsOtherPositive || '',
    Prosecutor_PreferredContact: p.Prosecutor_PreferredContact || '',
    Prosecutor_TextNumber: p.Prosecutor_TextNumber || '',
    Prosecutor_Active: p.Prosecutor_Active,
  };
}

const ManagerCourtView: React.FC = () => {
  const [searchParams] = useSearchParams();
  const courtGuid = searchParams.get('Court_guid');
  const navigate = useNavigate();
  const [court, setCourt] = useState<FullCourtDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Edit form state
  const [isEditing, setIsEditing] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({
    Court_Name: '',
    Court_Email: '',
    Court_Address: '',
    Court_Phone: '',
    Court_Fax: '',
    Court_County: '',
    Court_OffersOffDocket: false,
    Court_OfRecord: false,
    Court_FilingMethod: '' as CourtFilingMethod | '',
    Court_FilingAddress: '',
    Court_FilingNote: '',
    Court_JpPrecinct: '',
    Court_JpPlace: '',
    Court_Active: true
  });

  // Prosecutor modal state. With no selected prosecutor the dialog adds a new one, which is how
  // "Add Prosecutor" and "Duplicate" both work without leaving the court page (Lloyd, 6 Oct 2026:
  // "easy to add, duplicate, edit and delete").
  const [prosecutorModalOpen, setProsecutorModalOpen] = useState(false);
  const [selectedProsecutor, setSelectedProsecutor] = useState<ProsecutorDto | null>(null);
  const [prosecutorModalLoading, setProsecutorModalLoading] = useState(false);
  const [prosecutorEditForm, setProsecutorEditForm] = useState<ProsecutorForm>(EMPTY_PROSECUTOR_FORM);
  const [prosecutorModalError, setProsecutorModalError] = useState<string | null>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  // Lloyd's rating lists, from the server's one definition. The page works without them; only the
  // ratings card and the prosecutor ratings wait for them.
  const [traitLists, setTraitLists] = useState<TraitLists | null>(null);

  useEffect(() => {
    CourtApi.getTraits()
      .then(setTraitLists)
      .catch(() => setTraitLists(null));
  }, []);

  useEffect(() => {
    const fetchCourt = async () => {
      if (!courtGuid) {
        setError('Court ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const courtData = await ManagerApi.getCourt(courtGuid);
        setCourt(courtData);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch court details');
        setCourt(null);
      } finally {
        setLoading(false);
      }
    };

    fetchCourt();
  }, [courtGuid]);

  // Populate edit form when court data changes
  useEffect(() => {
    if (court) {
      setEditForm({
        Court_Name: court.Court_Name,
        Court_Email: court.Court_Email,
        Court_Address: court.Court_Address,
        Court_Phone: court.Court_Phone || '',
        Court_Fax: court.Court_Fax || '',
        Court_County: court.Court_County || '',
        Court_FilingMethod: court.Court_FilingMethod ?? '',
        Court_FilingAddress: court.Court_FilingAddress || '',
        Court_FilingNote: court.Court_FilingNote || '',
        Court_JpPrecinct: court.Court_JpPrecinct || '',
        Court_JpPlace: court.Court_JpPlace || '',
        Court_OffersOffDocket: !!court.Court_OffersOffDocket,
        Court_OfRecord: !!court.Court_OfRecord,
        Court_Active: court.Court_Active
      });
    }
  }, [court]);

  const handleBack = () => {
    navigate('/courts');
  };

  const handleEditCourt = () => {
    navigate(`/court/edit?Court_guid=${courtGuid}`);
  };

  const handleAddProsecutor = () => {
    setSelectedProsecutor(null);
    setProsecutorEditForm(EMPTY_PROSECUTOR_FORM);
    setProsecutorModalError(null);
    setShowDeleteConfirmation(false);
    setProsecutorModalOpen(true);
  };

  /** A new prosecutor with everything but the person copied, for an office that shares details. */
  const handleDuplicateProsecutor = () => {
    setSelectedProsecutor(null);
    setProsecutorEditForm((prev) => ({ ...prev, Prosecutor_Name: '', Prosecutor_Active: true }));
    setProsecutorModalError(null);
  };

  const handleEditFormChange = (field: string, value: string | boolean) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const handleStartEdit = () => {
    setIsEditing(true);
    setSuccessMessage(null);
    setError(null);
  };

  const handleSaveChanges = async () => {
    if (!courtGuid) return;

    try {
      setActionLoading(true);
      // The dropdown's blank option is an empty string, but "nobody has recorded this" is null on
      // the record. Sending '' would store a method the server does not recognise and the court
      // page would then render a blank where it should say "Not recorded".
      const updatedCourt = await ManagerApi.updateCourt(courtGuid, {
        ...editForm,
        Court_FilingMethod: editForm.Court_FilingMethod || null,
        // "File at" is HIDDEN when the method is email or unset, not cleared, so without this an
        // address typed against a previous method is still submitted and persists invisibly. A
        // court switched from fax to email would keep showing its old fax number to anyone who
        // later switched it back.
        Court_FilingAddress:
          editForm.Court_FilingMethod === '' || editForm.Court_FilingMethod === 'email'
            ? null
            : editForm.Court_FilingAddress.trim() || null,
        // Blank is "not recorded". A county-wide court has no places, so its place is cleared
        // here as well as on the server.
        Court_JpPrecinct: editForm.Court_JpPrecinct || null,
        Court_JpPlace:
          editForm.Court_JpPrecinct === '' || editForm.Court_JpPrecinct === 'countywide'
            ? null
            : editForm.Court_JpPlace.trim() || null,
      });
      setCourt(updatedCourt);
      setIsEditing(false);
      setSuccessMessage('Court updated successfully!');
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update court');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelEdit = () => {
    if (court) {
      setEditForm({
        Court_Name: court.Court_Name,
        Court_Email: court.Court_Email,
        Court_Address: court.Court_Address,
        Court_Phone: court.Court_Phone || '',
        Court_Fax: court.Court_Fax || '',
        Court_County: court.Court_County || '',
        Court_FilingMethod: court.Court_FilingMethod ?? '',
        Court_FilingAddress: court.Court_FilingAddress || '',
        Court_FilingNote: court.Court_FilingNote || '',
        Court_JpPrecinct: court.Court_JpPrecinct || '',
        Court_JpPlace: court.Court_JpPlace || '',
        Court_OffersOffDocket: !!court.Court_OffersOffDocket,
        Court_OfRecord: !!court.Court_OfRecord,
        Court_Active: court.Court_Active
      });
    }
    setIsEditing(false);
    setError(null);
  };

  // Prosecutor modal handlers
  const handleOpenProsecutorModal = (prosecutor: ProsecutorDto) => {
    setSelectedProsecutor(prosecutor);
    setProsecutorEditForm(prosecutorFormFrom(prosecutor));
    setProsecutorModalError(null);
    setProsecutorModalOpen(true);
    setShowDeleteConfirmation(false);
  };

  const handleCloseProsecutorModal = () => {
    setProsecutorModalOpen(false);
    setSelectedProsecutor(null);
    setShowDeleteConfirmation(false);
    setProsecutorModalError(null);
  };

  const handleProsecutorFormChange = <K extends keyof ProsecutorForm>(field: K, value: ProsecutorForm[K]) => {
    setProsecutorEditForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveProsecutor = async () => {
    if (!court || !courtGuid) return;

    // Blank text means "not recorded", which the server stores as null.
    const payload = {
      ...prosecutorEditForm,
      Prosecutor_Phone: prosecutorEditForm.Prosecutor_Phone.trim() || null,
      Prosecutor_Disposition: prosecutorEditForm.Prosecutor_Disposition || null,
      Prosecutor_PreferredContact: prosecutorEditForm.Prosecutor_PreferredContact || null,
      Prosecutor_TextNumber: prosecutorEditForm.Prosecutor_TextNumber.trim() || null,
    };

    try {
      setProsecutorModalLoading(true);
      setProsecutorModalError(null);
      if (selectedProsecutor) {
        const { prosecutor } = await ProsecutorApi.updateProsecutor(
          selectedProsecutor.Prosecutor_guid,
          payload,
        );
        setCourt({
          ...court,
          prosecutors: court.prosecutors.map((p) =>
            p.Prosecutor_guid === selectedProsecutor.Prosecutor_guid ? prosecutor : p,
          ),
        });
        setSuccessMessage('Prosecutor updated successfully!');
      } else {
        const created = await ManagerApi.createProsecutor({ ...payload, Court_guid: courtGuid });
        setCourt({ ...court, prosecutors: [...court.prosecutors, created] });
        setSuccessMessage(`${created.Prosecutor_Name} added.`);
      }
      handleCloseProsecutorModal();
    } catch (err) {
      setProsecutorModalError(err instanceof Error ? err.message : 'Failed to save prosecutor');
    } finally {
      setProsecutorModalLoading(false);
    }
  };

  const handleDeleteProsecutor = async () => {
    if (!selectedProsecutor) return;

    try {
      setProsecutorModalLoading(true);
      await ProsecutorApi.deleteProsecutor(selectedProsecutor.Prosecutor_guid);

      // Remove the prosecutor from the court data
      if (court) {
        const updatedProsecutors = court.prosecutors.filter(p => 
          p.Prosecutor_guid !== selectedProsecutor.Prosecutor_guid
        );
        setCourt({ ...court, prosecutors: updatedProsecutors });
      }

      setSuccessMessage('Prosecutor removed successfully!');
      handleCloseProsecutorModal();
    } catch (err) {
      setProsecutorModalError(err instanceof Error ? err.message : 'Failed to remove prosecutor');
    } finally {
      setProsecutorModalLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="manager-court-view">
        <Container maxWidth="lg" sx={{ mt: 3, mb: 4 }}>
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress />
          </Box>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div className="manager-court-view">
        <Container maxWidth="lg" sx={{ mt: 3, mb: 4 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
          >
            Back to Courts
          </Button>
        </Container>
      </div>
    );
  }

  if (!court) {
    return (
      <div className="manager-court-view">
        <Container maxWidth="lg" sx={{ mt: 3, mb: 4 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Court not found
          </Alert>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
          >
            Back to Courts
          </Button>
        </Container>
      </div>
    );
  }

  return (
    <div className="manager-court-view">
      
      <Container maxWidth="lg" sx={{ mt: 3, mb: 4 }}>
        <Box display="flex" alignItems="center" mb={3}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{ mr: 2 }}
          >
            Back to Courts
          </Button>
          
          <Box flexGrow={1}>
            <Typography variant="h4" component="h1" gutterBottom>
              {court.Court_Name}
            </Typography>
          </Box>
        </Box>

        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
            {successMessage}
          </Alert>
        )}

        {error && !loading && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' }, 
            gap: 3,
            mb: 3 
          }}
        >
          {/* Court Details Card */}
          <Box sx={{ flex: 1 }}>
            <Card elevation={3}>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                    <AccountBalanceIcon />
                  </Avatar>
                  <Typography variant="h6" component="h2">
                    Court Information
                  </Typography>
                  {isEditing ? (
                    <Box ml="auto" display="flex" gap={1}>
                      <IconButton 
                        onClick={handleSaveChanges} 
                        size="small" 
                        color="primary" 
                        disabled={actionLoading}
                        title="Save changes"
                      >
                        <SaveIcon />
                      </IconButton>
                      <IconButton 
                        onClick={handleCancelEdit} 
                        size="small" 
                        color="secondary" 
                        disabled={actionLoading}
                        title="Cancel changes"
                      >
                        <CancelIcon />
                      </IconButton>
                    </Box>
                  ) : (
                    <Box ml="auto" display="flex" gap={1}>
                      <IconButton 
                        onClick={handleStartEdit} 
                        size="small"
                        sx={{ height: 'fit-content' }}
                      >
                        <EditIcon />
                      </IconButton>
                    </Box>
                  )}
                </Box>
                
                <Divider sx={{ mb: 2 }} />
                
                {isEditing ? (
                  // Edit mode form
                  <Box display="flex" flexDirection="column" gap={2}>
                    <TextField
                      label="Court Name"
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={editForm.Court_Name}
                      onChange={(e) => handleEditFormChange('Court_Name', e.target.value)}
                      disabled={actionLoading}
                    />
                    
                    <TextField
                      label="Court Email"
                      variant="outlined"
                      size="small"
                      fullWidth
                      type="email"
                      value={editForm.Court_Email}
                      onChange={(e) => handleEditFormChange('Court_Email', e.target.value)}
                      disabled={actionLoading}
                    />
                    
                    <TextField
                      label="Court Phone"
                      variant="outlined"
                      size="small"
                      fullWidth
                      type="tel"
                      value={editForm.Court_Phone}
                      onChange={(e) => handleEditFormChange('Court_Phone', e.target.value)}
                      disabled={actionLoading}
                      placeholder="Optional phone number"
                    />

                    <TextField
                      label="Court Fax"
                      variant="outlined"
                      size="small"
                      fullWidth
                      type="tel"
                      value={editForm.Court_Fax}
                      onChange={(e) => handleEditFormChange('Court_Fax', e.target.value)}
                      disabled={actionLoading}
                      placeholder="Optional fax number"
                    />
                    
                    <TextField
                      label="Court Address"
                      variant="outlined"
                      size="small"
                      fullWidth
                      multiline
                      rows={2}
                      value={editForm.Court_Address}
                      onChange={(e) => handleEditFormChange('Court_Address', e.target.value)}
                      disabled={actionLoading}
                    />

                    <TextField
                      label="County"
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={editForm.Court_County}
                      onChange={(e) => handleEditFormChange('Court_County', e.target.value)}
                      disabled={actionLoading}
                    />

                    {/*
                      How this court takes a filing (Maria and Hannah, 18 Sep 2026). Until this
                      existed there was nowhere to record it, so staff typed it into the email
                      field, which then sent the letter to an address nobody reads.

                      Blank is a real answer and the default. It means nobody has recorded how this
                      court works, which is different from saying it takes email.
                    */}
                    <TextField
                      select
                      label="Takes filings by"
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={editForm.Court_FilingMethod}
                      onChange={(e) => handleEditFormChange('Court_FilingMethod', e.target.value)}
                      disabled={actionLoading}
                      helperText="Leave blank if nobody has checked how this court wants filings"
                    >
                      <MenuItem value="">Not recorded</MenuItem>
                      {(Object.keys(COURT_FILING_METHOD_LABELS) as CourtFilingMethod[]).map((method) => (
                        <MenuItem key={method} value={method}>
                          {COURT_FILING_METHOD_LABELS[method]}
                        </MenuItem>
                      ))}
                    </TextField>

                    {/* Kept apart from the court's email so a court that files elsewhere still has
                        a working mailbox for everything else. That separation is what Laredo lost
                        when its email was pointed at the internal box. */}
                    {editForm.Court_FilingMethod !== '' && editForm.Court_FilingMethod !== 'email' && (
                      <TextField
                        label="File at"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={editForm.Court_FilingAddress}
                        onChange={(e) => handleEditFormChange('Court_FilingAddress', e.target.value)}
                        disabled={actionLoading}
                        helperText="The website, fax number or postal address to file at"
                      />
                    )}

                    <TextField
                      label="Filing notes"
                      variant="outlined"
                      size="small"
                      fullWidth
                      multiline
                      rows={2}
                      value={editForm.Court_FilingNote}
                      onChange={(e) => handleEditFormChange('Court_FilingNote', e.target.value)}
                      disabled={actionLoading}
                      helperText="Anything else about how this court wants things sent"
                    />

                    {/*
                      A JP court's precinct (Lloyd and Kelsi, 5 Oct 2026). The JP bonds will not
                      generate without one, and the bonds and motions print it in the caption.
                      Blank falls back to a precinct in the court name, e.g. "Bexar JP 3".
                    */}
                    <Box display="flex" gap={2}>
                      <TextField
                        select
                        label="JP precinct"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={editForm.Court_JpPrecinct}
                        onChange={(e) => handleEditFormChange('Court_JpPrecinct', e.target.value)}
                        disabled={actionLoading}
                        helperText="JP courts only. Leave blank to read it from the court name"
                      >
                        <MenuItem value="">Not recorded</MenuItem>
                        {JP_PRECINCT_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                      {editForm.Court_JpPrecinct !== '' && editForm.Court_JpPrecinct !== 'countywide' && (
                        <TextField
                          label="Place"
                          variant="outlined"
                          size="small"
                          fullWidth
                          value={editForm.Court_JpPlace}
                          onChange={(e) =>
                            handleEditFormChange('Court_JpPlace', e.target.value.replace(/\D/g, '').slice(0, 2))
                          }
                          disabled={actionLoading}
                          helperText="Optional. Precinct 1, Place 2 prints as PRECINCT 1, PLACE 2"
                        />
                      )}
                    </Box>

                    <FormControlLabel
                      control={
                        <Switch
                          checked={editForm.Court_OffersOffDocket}
                          onChange={(e) => handleEditFormChange('Court_OffersOffDocket', e.target.checked)}
                          disabled={actionLoading}
                        />
                      }
                      label="Offers off docket"
                    />

                    <FormControlLabel
                      control={
                        <Switch
                          checked={editForm.Court_OfRecord}
                          onChange={(e) => handleEditFormChange('Court_OfRecord', e.target.checked)}
                          disabled={actionLoading}
                        />
                      }
                      label="Court of record"
                    />

                    <FormControlLabel
                      control={
                        <Switch
                          checked={editForm.Court_Active}
                          onChange={(e) => handleEditFormChange('Court_Active', e.target.checked)}
                          disabled={actionLoading}
                        />
                      }
                      label="Active"
                    />
                    
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Court ID
                      </Typography>
                      <Typography variant="body2" color="text.disabled" sx={{ fontFamily: 'monospace' }}>
                        {court.Court_guid}
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  // View mode
                  <>
                    <Box mb={2}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Status
                      </Typography>
                      <Chip
                        label={court.Court_Active ? 'Active' : 'Inactive'}
                        color={court.Court_Active ? 'success' : 'error'}
                        size="small"
                      />
                    </Box>
                    
                    <Box mb={2} display="flex" alignItems="flex-start">
                      <EmailIcon sx={{ mr: 1, mt: 0.5, color: 'text.secondary' }} />
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Email
                        </Typography>
                        <Typography variant="body1">
                          {court.Court_Email}
                        </Typography>
                      </Box>
                    </Box>
                    
                    {court.Court_Phone && (
                      <Box mb={2} display="flex" alignItems="flex-start">
                        <PhoneIcon sx={{ mr: 1, mt: 0.5, color: 'text.secondary' }} />
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            Phone
                          </Typography>
                          <Typography variant="body1">
                            {court.Court_Phone}
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {court.Court_Fax && (
                      <Box mb={2} display="flex" alignItems="flex-start">
                        <PrintIcon sx={{ mr: 1, mt: 0.5, color: 'text.secondary' }} />
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            Fax
                          </Typography>
                          <Typography variant="body1">
                            {court.Court_Fax}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    
                    <Box mb={2} display="flex" alignItems="flex-start">
                      <LocationOnIcon sx={{ mr: 1, mt: 0.5, color: 'text.secondary' }} />
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Address
                        </Typography>
                        <Typography variant="body1">
                          {court.Court_Address}
                        </Typography>
                      </Box>
                    </Box>

                    <Box mb={2}>
                      <Typography variant="subtitle2" color="text.secondary">
                        County
                      </Typography>
                      <Typography variant="body1">
                        {court.Court_County || '—'}
                      </Typography>
                    </Box>

                    {describeJpPrecinct(court.Court_JpPrecinct, court.Court_JpPlace) && (
                      <Box mb={2}>
                        <Typography variant="subtitle2" color="text.secondary">
                          JP precinct
                        </Typography>
                        <Typography variant="body1">
                          {describeJpPrecinct(court.Court_JpPrecinct, court.Court_JpPlace)}
                        </Typography>
                      </Box>
                    )}

                    {/* How this court takes a filing. Shown whenever it is recorded, because the
                        person about to send a letter is the one who needs it. */}
                    <Box mb={2}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Takes filings by
                      </Typography>
                      <Typography variant="body1">
                        {court.Court_FilingMethod
                          ? COURT_FILING_METHOD_LABELS[court.Court_FilingMethod]
                          : 'Not recorded'}
                      </Typography>
                      {court.Court_FilingAddress && (
                        <Typography variant="body2" color="text.secondary">
                          File at {court.Court_FilingAddress}
                        </Typography>
                      )}
                      {court.Court_FilingNote && (
                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                          {court.Court_FilingNote}
                        </Typography>
                      )}
                    </Box>

                    <Box mb={2} display="flex" gap={1} flexWrap="wrap">
                      <Chip
                        label={`Offers off docket: ${court.Court_OffersOffDocket == null ? 'Unknown' : court.Court_OffersOffDocket ? 'Yes' : 'No'}`}
                        size="small"
                        variant="outlined"
                      />
                      <Chip
                        label={`Court of record: ${court.Court_OfRecord == null ? 'Unknown' : court.Court_OfRecord ? 'Yes' : 'No'}`}
                        size="small"
                        variant="outlined"
                      />
                    </Box>

                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Court ID
                      </Typography>
                      <Typography variant="body2" color="text.disabled" sx={{ fontFamily: 'monospace' }}>
                        {court.Court_guid}
                      </Typography>
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>
          </Box>
          
          {/* Prosecutors Card */}
          <Box sx={{ flex: 1 }}>
            <Card elevation={3} sx={{ height: '100%' }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Box display="flex" alignItems="center">
                    <Avatar sx={{ mr: 2, bgcolor: 'secondary.main' }}>
                      <PersonIcon />
                    </Avatar>
                    <Typography variant="h6" component="h2">
                      Prosecutors ({court.prosecutors.length})
                    </Typography>
                  </Box>
                  
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleAddProsecutor}
                  >
                    Add Prosecutor
                  </Button>
                </Box>
                
                <Divider sx={{ mb: 2 }} />
                
                {court.prosecutors.length === 0 ? (
                  <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                    No prosecutors assigned to this court.
                  </Typography>
                ) : (
                  <List dense>
                    {court.prosecutors.map((prosecutor, index) => (
                      <React.Fragment key={prosecutor.Prosecutor_guid}>
                        <ListItem
                          sx={{
                            px: 0,
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: 'action.hover',
                              borderRadius: 1,
                            },
                          }}
                          onClick={() => handleOpenProsecutorModal(prosecutor)}
                        >
                          <ListItemText
                            primary={
                              <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Typography variant="subtitle1">
                                  {prosecutor.Prosecutor_Name}
                                </Typography>
                                <Box display="flex" gap={0.5}>
                                  {prosecutor.Prosecutor_Disposition && (
                                    <Chip
                                      label={prosecutor.Prosecutor_Disposition}
                                      color={prosecutor.Prosecutor_Disposition === 'Friendly' ? 'success' : prosecutor.Prosecutor_Disposition === 'Difficult' ? 'warning' : 'default'}
                                      size="small"
                                      variant="outlined"
                                    />
                                  )}
                                  <Chip
                                    label={prosecutor.Prosecutor_Active ? 'Active' : 'Inactive'}
                                    color={prosecutor.Prosecutor_Active ? 'success' : 'error'}
                                    size="small"
                                  />
                                </Box>
                              </Box>
                            }
                            secondary={
                              <Box>
                                <Typography variant="body2" color="text.secondary">
                                  {prosecutor.Prosecutor_Email}
                                </Typography>
                                {prosecutor.Prosecutor_Phone && (
                                  <Typography variant="body2" color="text.secondary">
                                    {prosecutor.Prosecutor_Phone}
                                  </Typography>
                                )}
                                {(prosecutor.Prosecutor_PreferredContact || prosecutor.Prosecutor_TextNumber) && (
                                  <Typography variant="body2" color="text.secondary">
                                    {prosecutor.Prosecutor_PreferredContact &&
                                      `Prefers ${PROSECUTOR_CONTACT_LABELS[prosecutor.Prosecutor_PreferredContact].toLowerCase()}`}
                                    {prosecutor.Prosecutor_PreferredContact && prosecutor.Prosecutor_TextNumber && ' · '}
                                    {prosecutor.Prosecutor_TextNumber && `Text ${prosecutor.Prosecutor_TextNumber}`}
                                  </Typography>
                                )}
                                {traitLists && (prosecutor.Prosecutor_Traits?.length ?? 0) > 0 && (
                                  <Box display="flex" flexWrap="wrap" gap={0.5} my={0.5}>
                                    {traitLists.prosecutor
                                      .filter((t) => prosecutor.Prosecutor_Traits?.includes(t.key))
                                      .map((t) => (
                                        <Chip
                                          key={t.key}
                                          label={t.label}
                                          size="small"
                                          color={t.polarity === 'negative' ? 'warning' : 'success'}
                                          variant="outlined"
                                        />
                                      ))}
                                  </Box>
                                )}
                                <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace' }}>
                                  ID: {prosecutor.Prosecutor_guid}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                        
                        {index < court.prosecutors.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* Lloyd's court ratings and who has covered here (6 Oct 2026). */}
        {traitLists && (
          <Box sx={{ mb: 3 }}>
            <CourtRatingsCard court={court} definitions={traitLists.court} onSaved={setCourt} />
          </Box>
        )}

        <Card elevation={3} sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" mb={2}>
              <Avatar sx={{ mr: 2, bgcolor: 'info.main' }}>
                <GavelIcon />
              </Avatar>
              <Typography variant="h6" component="h2">
                Covering attorneys
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <CourtCoveringAttorneys courtGuid={court.Court_guid} />
          </CardContent>
        </Card>

        {/*
          The docket for this court (Lloyd, 16 Sep 2026). Full width under the two cards, because it
          is a table rather than a detail panel, and it is the part somebody covering the courtroom
          actually came here for.
        */}
        <Box sx={{ mt: 3 }}>
          <CourtClientsPanel Court_guid={court.Court_guid} />
        </Box>

      </Container>

      {/* Prosecutor Edit Modal */}
      <Dialog
        open={prosecutorModalOpen}
        onClose={handleCloseProsecutorModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {showDeleteConfirmation ? 'Confirm Delete' : selectedProsecutor ? 'Edit Prosecutor' : 'Add Prosecutor'}
        </DialogTitle>
        
        <DialogContent>
          {prosecutorModalError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setProsecutorModalError(null)}>
              {prosecutorModalError}
            </Alert>
          )}
          {showDeleteConfirmation ? (
            <DialogContentText>
              Are you sure you want to remove prosecutor "{selectedProsecutor?.Prosecutor_Name}"? 
              This action will set their status to inactive.
            </DialogContentText>
          ) : (
            <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 1 }}>
              <TextField
                label="Prosecutor Name"
                variant="outlined"
                size="small"
                fullWidth
                value={prosecutorEditForm.Prosecutor_Name}
                onChange={(e) => handleProsecutorFormChange('Prosecutor_Name', e.target.value)}
                disabled={prosecutorModalLoading}
              />
              
              <TextField
                label="Prosecutor Email"
                variant="outlined"
                size="small"
                fullWidth
                type="email"
                value={prosecutorEditForm.Prosecutor_Email}
                onChange={(e) => handleProsecutorFormChange('Prosecutor_Email', e.target.value)}
                disabled={prosecutorModalLoading}
              />

              <TextField
                label="Prosecutor Phone"
                variant="outlined"
                size="small"
                fullWidth
                type="tel"
                value={prosecutorEditForm.Prosecutor_Phone}
                onChange={(e) => handleProsecutorFormChange('Prosecutor_Phone', e.target.value)}
                disabled={prosecutorModalLoading}
                placeholder="Optional phone number"
              />

              <FormControl fullWidth size="small" disabled={prosecutorModalLoading}>
                <InputLabel id="disposition-label">Disposition</InputLabel>
                <Select
                  labelId="disposition-label"
                  label="Disposition"
                  value={prosecutorEditForm.Prosecutor_Disposition}
                  onChange={(e) => handleProsecutorFormChange('Prosecutor_Disposition', e.target.value)}
                >
                  <MenuItem value=""><em>Not specified</em></MenuItem>
                  <MenuItem value="Friendly">Friendly</MenuItem>
                  <MenuItem value="Difficult">Difficult</MenuItem>
                </Select>
              </FormControl>

              {/* Lloyd: "how she prefers to be contacted", and a number for texting. */}
              <Box display="flex" gap={2}>
                <TextField
                  select
                  label="Prefers to be contacted by"
                  size="small"
                  fullWidth
                  value={prosecutorEditForm.Prosecutor_PreferredContact}
                  onChange={(e) =>
                    handleProsecutorFormChange(
                      'Prosecutor_PreferredContact',
                      e.target.value as ProsecutorContactPreference | '',
                    )
                  }
                  disabled={prosecutorModalLoading}
                >
                  <MenuItem value=""><em>Not known</em></MenuItem>
                  {(Object.keys(PROSECUTOR_CONTACT_LABELS) as ProsecutorContactPreference[]).map((c) => (
                    <MenuItem key={c} value={c}>{PROSECUTOR_CONTACT_LABELS[c]}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="Texting number"
                  size="small"
                  fullWidth
                  type="tel"
                  value={prosecutorEditForm.Prosecutor_TextNumber}
                  onChange={(e) => handleProsecutorFormChange('Prosecutor_TextNumber', e.target.value)}
                  disabled={prosecutorModalLoading}
                  placeholder="If different from the phone"
                />
              </Box>

              {traitLists && (
                <TraitPicker
                  definitions={traitLists.prosecutor}
                  selected={prosecutorEditForm.Prosecutor_Traits}
                  onChange={(traits) => handleProsecutorFormChange('Prosecutor_Traits', traits)}
                  otherNegative={prosecutorEditForm.Prosecutor_TraitsOtherNegative}
                  otherPositive={prosecutorEditForm.Prosecutor_TraitsOtherPositive}
                  onOtherChange={(side, value) =>
                    handleProsecutorFormChange(
                      side === 'negative' ? 'Prosecutor_TraitsOtherNegative' : 'Prosecutor_TraitsOtherPositive',
                      value,
                    )
                  }
                  disabled={prosecutorModalLoading}
                />
              )}

              <FormControlLabel
                control={
                  <Switch
                    checked={prosecutorEditForm.Prosecutor_Active}
                    onChange={(e) => handleProsecutorFormChange('Prosecutor_Active', e.target.checked)}
                    disabled={prosecutorModalLoading}
                  />
                }
                label="Active"
              />
              
              {selectedProsecutor && (
                <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace' }}>
                  ID: {selectedProsecutor.Prosecutor_guid}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          {showDeleteConfirmation ? (
            <>
              <Button 
                onClick={() => setShowDeleteConfirmation(false)} 
                disabled={prosecutorModalLoading}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleDeleteProsecutor} 
                color="error" 
                variant="contained"
                disabled={prosecutorModalLoading}
                startIcon={prosecutorModalLoading ? <CircularProgress size={16} /> : <DeleteIcon />}
              >
                {prosecutorModalLoading ? 'Removing...' : 'Remove'}
              </Button>
            </>
          ) : (
            <>
              <Button 
                onClick={handleCloseProsecutorModal} 
                disabled={prosecutorModalLoading}
              >
                Cancel
              </Button>
              {selectedProsecutor && (
                <>
                  <Button
                    onClick={() => setShowDeleteConfirmation(true)}
                    color="error"
                    disabled={prosecutorModalLoading}
                    startIcon={<DeleteIcon />}
                  >
                    Remove
                  </Button>
                  <Button
                    onClick={handleDuplicateProsecutor}
                    disabled={prosecutorModalLoading}
                    startIcon={<ContentCopyIcon />}
                    title="Add another prosecutor with these details"
                  >
                    Duplicate
                  </Button>
                </>
              )}
              <Button 
                onClick={handleSaveProsecutor} 
                variant="contained"
                disabled={prosecutorModalLoading}
                startIcon={prosecutorModalLoading ? <CircularProgress size={16} /> : <SaveIcon />}
              >
                {prosecutorModalLoading ? 'Saving...' : selectedProsecutor ? 'Save' : 'Add'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ManagerCourtView;
